import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "friday",
    q: "It's Friday, 9pm. Where are you?",
    options: [
      { label: "Planning tomorrow's sunrise hike", emoji: "🥾", pts: 2 },
      { label: "Deep in a board game battle or a proper conversation", emoji: "🎲", pts: 2 },
      { label: "Home, but doom-scrolling till 2am", emoji: "📱", pts: 1 },
      { label: "Third bar of the night, just warming up", emoji: "🍺", pts: 0 },
    ],
  },
  {
    id: "alcohol",
    q: "Your relationship with alcohol:",
    options: [
      { label: "Sober and loving it", emoji: "✨", pts: 2 },
      { label: "Sober-curious — a proper break sounds amazing", emoji: "🌱", pts: 2 },
      { label: "Social drinker, could take it or leave it", emoji: "🤷", pts: 1 },
      { label: "It's basically my personality", emoji: "🍹", pts: 0 },
    ],
  },
  {
    id: "shopping",
    q: "A housemate hits the button: \"Going shopping in 15 min 🛒\". You:",
    options: [
      { label: "Reply \"oat milk please\" within seconds", emoji: "⚡", pts: 2 },
      { label: "Jump in the car — impromptu road trip", emoji: "🚗", pts: 2 },
      { label: "See it three hours later", emoji: "😴", pts: 1 },
      { label: "Mute the group", emoji: "🔇", pts: 0 },
    ],
  },
  {
    id: "goals",
    q: "Monday morning: the house declares weekly goals out loud. You feel:",
    options: [
      { label: "Fired up — accountability is my love language", emoji: "🔥", pts: 2 },
      { label: "Nervous, but I know it's exactly what I need", emoji: "😬", pts: 2 },
      { label: "I'll say something vague and hope nobody follows up", emoji: "🫥", pts: 1 },
      { label: "Physically ill", emoji: "🤢", pts: 0 },
    ],
  },
  {
    id: "dogs",
    q: "A dog rests its head on your lap mid-deep-work:",
    options: [
      { label: "Meeting cancelled. This is my life now", emoji: "🐕", pts: 2 },
      { label: "One-handed typing, obviously", emoji: "⌨️", pts: 2 },
      { label: "Gently redirect after one minute of pats", emoji: "⏱️", pts: 1 },
      { label: "Dogs? Inside? Absolutely not", emoji: "🚫", pts: 0 },
    ],
  },
  {
    id: "building",
    q: "What are you building right now?",
    options: [
      { label: "A business or creative project I actually care about", emoji: "🚀", pts: 2 },
      { label: "A better version of myself", emoji: "🌄", pts: 2 },
      { label: "A remote career with real work-life balance", emoji: "💻", pts: 2 },
      { label: "Mostly excuses", emoji: "🙃", pts: 1 },
    ],
  },
  {
    id: "dinner",
    q: "Family dinner in the courtyard. Your role:",
    options: [
      { label: "Head chef — it's paella night", emoji: "🥘", pts: 2 },
      { label: "Sous chef / chief washer-upper", emoji: "🧽", pts: 2 },
      { label: "Quality control — I bring the conversation", emoji: "🗣️", pts: 1 },
      { label: "Eating alone in my room", emoji: "🚪", pts: 0 },
    ],
  },
  {
    id: "morning",
    q: "Your ideal morning:",
    options: [
      { label: "Sunrise yoga on the sky terrace", emoji: "🧘", pts: 2 },
      { label: "Coffee, laptop, deep work by 9", emoji: "☕", pts: 2 },
      { label: "A slow walk into the mountains", emoji: "⛰️", pts: 2 },
      { label: "It starts at 1pm", emoji: "🌙", pts: 0 },
    ],
  },
];

const ROOMS = [
  "Not sure yet",
  "Garden Room",
  "Sky Room",
  "Panorama Double",
  "Tower Room",
  "Junior Suite (Baylight)",
  "Luxury Suite",
  "Apartment",
];

const getOutcome = (score) => {
  if (score >= 12)
    return {
      title: "Certified Savi",
      emoji: "🌞",
      text: "That wasn't a quiz — that was a mirror. Sunrise energy, sober by choice, first to reply to the shopping run, and a dog's favourite human. You wouldn't just fit in at Savi… there's a strong chance you're already one of us and nobody told you. The villa is basically waiting.",
    };
  if (score >= 7)
    return {
      title: "Savi-Curious",
      emoji: "🌱",
      text: "There's a Savi in you trying to get out. You love the idea of clear mornings, real conversations and people who actually follow up on their goals — you've just never lived somewhere that made it easy. That's literally what the environment is for. One month here and the quiz result changes itself.",
    };
  return {
    title: "Wild Card",
    emoji: "🃏",
    text: "Honestly? A month at Savi might flip your whole life — precisely because none of this comes naturally to you yet. The 1pm mornings, the muted group chats… we've seen that person arrive before. They leave doing sunrise hikes and lecturing their friends about accountability. Dangerous stuff. Proceed accordingly.",
  };
};

export default function AreYouSaviPage() {
  const [step, setStep] = useState("intro"); // intro | quiz | form | result
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", age: "", room: "Not sure yet", dates: "" });
  const [submitting, setSubmitting] = useState(false);

  const score = Object.values(answers).reduce((s, a) => s + a.pts, 0);
  const outcome = getOutcome(score);

  const pick = (opt) => {
    const q = QUESTIONS[qIndex];
    setAnswers((prev) => ({ ...prev, [q.id]: { question: q.q, answer: opt.label, pts: opt.pts } }));
    if (qIndex < QUESTIONS.length - 1) {
      setQIndex(qIndex + 1);
    } else {
      setStep("form");
    }
  };

  const goBack = () => {
    if (qIndex > 0) setQIndex(qIndex - 1);
    else setStep("intro");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.email.includes("@")) {
      toast.error("We need at least your name and a real email to reveal your result 😄");
      return;
    }
    if (form.phone.trim().replace(/[^\d+]/g, "").length < 7) {
      toast.error("Pop in your phone number too — Steve's a talker, not a spammer 📞");
      return;
    }
    const age = parseInt(form.age, 10);
    if (!age || age < 16 || age > 99) {
      toast.error("And your age — we promise it's just curiosity, not a bouncer 🎂");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz/savi-check`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        age: form.age.trim(),
        room: form.room,
        dates: form.dates.trim(),
        score,
        outcome: outcome.title,
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, `${v.answer} (${v.pts} pts)`])),
      });
      setStep("result");
    } catch {
      toast.error("Something hiccuped — try again?");
    }
    setSubmitting(false);
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Are you Savi?", text: "Take the quiz — find out if you'd survive (thrive) at Savi Coliving.", url });
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — send it to someone suspicious 🕵️");
    } catch {
      toast(url);
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col" data-testid="are-you-savi-page">
      <div
        className="fixed inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url(/images/quiz-bg.jpg)" }}
        aria-hidden="true"
      />
      <div className="fixed inset-0 bg-gradient-to-b from-[#2E2A26]/50 via-[#2E2A26]/30 to-[#2E2A26]/60" aria-hidden="true" />
      <Helmet>
        <title>Are You Savi? | The 60-Second Fit Quiz</title>
        <meta name="description" content="8 questions. No wrong answers (some wrong answers). Find out if you'd fit in at Savi Coliving — a curated sober villa community in the mountains of Valencia." />
        <meta property="og:title" content="Are You Savi? Take the quiz." />
        <meta property="og:description" content="Sunrise hikes, sober mornings, resident dogs and radical accountability. Would you survive it — or secretly love it?" />
        <meta property="og:image" content={`${typeof window !== "undefined" ? window.location.origin : ""}/images/quiz-bg.jpg`} />
      </Helmet>

      <div className="relative flex-1 flex flex-col items-center justify-center px-5 py-10">
        <div className="w-full max-w-lg bg-[#FBF7F1]/95 backdrop-blur-md rounded-3xl shadow-2xl p-6 sm:p-9">

          {step === "intro" && (
            <div className="text-center animate-slide-up" data-testid="quiz-intro">
              <p className="text-[#C85A38] font-label text-xs tracking-[0.25em] uppercase mb-4">The official fit test</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-[#2E2A26] leading-tight mb-4">
                Are you <span className="italic">Savi</span>?
              </h1>
              <p className="text-[#2E2A26]/60 text-sm sm:text-base leading-relaxed mb-2 max-w-md mx-auto">
                8 questions. 60 seconds. No wrong answers.
              </p>
              <p className="text-[#2E2A26]/40 text-xs mb-8">(Okay — some wrong answers.)</p>
              <button
                onClick={() => setStep("quiz")}
                className="bg-[#C85A38] hover:bg-[#B04A2C] text-white font-medium px-10 py-4 rounded-full text-base transition-colors shadow-lg"
                data-testid="quiz-start-btn"
              >
                Find out →
              </button>
              <p className="text-[#2E2A26]/35 text-[11px] mt-6">
                A curated sober villa community · Mountains of Valencia
              </p>
            </div>
          )}

          {step === "quiz" && (
            <div className="animate-slide-up" key={qIndex} data-testid="quiz-question-screen">
              <div className="flex items-center justify-between mb-6">
                <button onClick={goBack} className="text-[#2E2A26]/40 hover:text-[#2E2A26] flex items-center gap-1 text-xs" data-testid="quiz-back-btn">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="flex gap-1.5">
                  {QUESTIONS.map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all ${i === qIndex ? "w-6 bg-[#C85A38]" : i < qIndex ? "w-1.5 bg-[#C85A38]/50" : "w-1.5 bg-[#2E2A26]/10"}`} />
                  ))}
                </div>
                <span className="text-[#2E2A26]/40 text-xs">{qIndex + 1}/{QUESTIONS.length}</span>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] leading-snug mb-6">
                {QUESTIONS[qIndex].q}
              </h2>
              <div className="space-y-3">
                {QUESTIONS[qIndex].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => pick(opt)}
                    className="w-full flex items-center gap-3 bg-white border border-stone-200 hover:border-[#C85A38] hover:shadow-md rounded-2xl px-4 py-3.5 text-left transition-all group"
                    data-testid={`quiz-option-${i}`}
                  >
                    <span className="text-xl flex-shrink-0">{opt.emoji}</span>
                    <span className="text-sm text-[#2E2A26]/80 group-hover:text-[#2E2A26]">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "form" && (
            <form onSubmit={submit} className="animate-slide-up" data-testid="quiz-lead-form">
              <p className="text-[#C85A38] font-label text-xs tracking-[0.25em] uppercase text-center mb-3">Results are in…</p>
              <h2 className="font-serif text-3xl text-[#2E2A26] text-center mb-2">Unlock your verdict</h2>
              <p className="text-[#2E2A26]/50 text-xs text-center mb-7">Tell us who you are and we'll reveal whether you're Savi material.</p>
              <div className="space-y-3">
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *"
                  className="w-full h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#C85A38]" data-testid="quiz-name-input" />
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *"
                  className="w-full h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#C85A38]" data-testid="quiz-email-input" />
                <div className="flex gap-3">
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone / WhatsApp *"
                    className="flex-1 min-w-0 h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#C85A38]" data-testid="quiz-phone-input" />
                  <input type="number" min="16" max="99" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="Age *"
                    className="w-24 h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#C85A38]" data-testid="quiz-age-input" />
                </div>
                <select value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm text-[#2E2A26]/80 focus:outline-none focus:border-[#C85A38]" data-testid="quiz-room-select">
                  {ROOMS.map((r) => (
                    <option key={r} value={r}>{r === "Not sure yet" ? "Which room catches your eye?" : r}</option>
                  ))}
                </select>
                <input type="text" value={form.dates} onChange={(e) => setForm({ ...form, dates: e.target.value })} placeholder="Dates you're eyeing (e.g. Oct–Nov)"
                  className="w-full h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#C85A38]" data-testid="quiz-dates-input" />
                <button type="submit" disabled={submitting}
                  className="w-full h-13 bg-[#C85A38] hover:bg-[#B04A2C] text-white font-medium py-3.5 rounded-full transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="quiz-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reveal my result <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[#2E2A26]/35 text-[10px] text-center">Your answers go straight to Steve — no spam, no mailing lists, just a human.</p>
              </div>
            </form>
          )}

          {step === "result" && (
            <div className="text-center animate-slide-up" data-testid="quiz-result">
              <span className="text-6xl block mb-4">{outcome.emoji}</span>
              <p className="text-[#C85A38] font-label text-xs tracking-[0.25em] uppercase mb-2">Your verdict</p>
              <h2 className="font-serif text-4xl text-[#2E2A26] mb-1" data-testid="quiz-result-title">{outcome.title}</h2>
              <p className="text-[#2E2A26]/40 text-xs mb-6">{score}/16 Savi points</p>
              <p className="text-[#2E2A26]/70 text-sm leading-relaxed max-w-md mx-auto mb-8">{outcome.text}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
                <a href="https://wa.me/34632628051?text=Hi%20Steve!%20I%20just%20took%20the%20Are%20You%20Savi%20quiz%20%F0%9F%8C%9E"
                  target="_blank" rel="noopener noreferrer"
                  className="bg-[#C85A38] hover:bg-[#B04A2C] text-white font-medium px-8 py-3.5 rounded-full text-sm transition-colors"
                  data-testid="quiz-whatsapp-cta">
                  Talk to Steve on WhatsApp
                </a>
                <Link to="/rooms" className="bg-white border border-stone-200 hover:border-[#C85A38] text-[#2E2A26] font-medium px-8 py-3.5 rounded-full text-sm transition-colors" data-testid="quiz-rooms-cta">
                  Peek at the rooms
                </Link>
              </div>
              <button onClick={share} className="text-[#2E2A26]/45 hover:text-[#2E2A26] text-xs inline-flex items-center gap-1.5" data-testid="quiz-share-btn">
                <Share2 className="w-3.5 h-3.5" /> Challenge a friend — send them the quiz
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="relative text-center text-white/60 text-[11px] pb-6 drop-shadow">
        <Link to="/" className="hover:text-white">savicoliving.com</Link> · Náquera, Valencia
      </p>
    </div>
  );
}
